import Step from 'components/global/Step'
import { useEffect, useState } from 'react'
import Box from 'components/shared/Box'
import Text from 'components/shared/Text'
import AttractionsInfo from 'components/shared/AttractionsInfo'
import { FormattedMessage, useIntl } from 'react-intl'
import { useCart } from 'store/cart-slice/hooks'
import { useAppSelector, useAppDispatch } from 'store/hooks'
import { addUpsell } from 'store/upsell-slice'
import {
    getDatesRange,
    RouteType,
    synchronizeTicketsWithCart,
} from 'store/transport-slice'
import AvailableOnSite from './AvailableOnSite'
import AvailableOnline from './AvailableOnline'
import NotFinishedUpsellInfo from './NotFinishedUpsellInfo'
import { CabanaItemType, synchronizeCabanasWithCart } from 'store/cabana-slice'
import { useApp } from 'store/steps'
import { pushDataLayer } from 'helpers/helpers'
import { UpsellsContext } from './utils/upsellsContext'

const Upsell = () => {
    const dispatch = useAppDispatch()
    const { formatMessage } = useIntl()
    const { isCreatorAccommodation } = useApp()
    const { isCartEmpty, isStepEmpty, removeNextStepsFromCart } = useCart()

    const [isEditingSpaInProgress, setIsEditingSpaInProgress] = useState(false)

    const {
        tickets: cartTickets,
        busTickets,
        cabanas,
        memoryCapture,
    } = useAppSelector((state) => state.cart.items)
    const { people: parkTickets, dates: parkDates } = useAppSelector(
        (state) => state.ticket
    )

    const areTicketsEmpty = isStepEmpty('tickets')

    useEffect(() => {
        removeNextStepsFromCart({ activeStepId: 'upsell' })

        // restore bus tickets
        if (busTickets.length > 0) {
            const routesFromCart: RouteType[] = busTickets.map((ticket) => ({
                ticket: {
                    ...ticket,
                    detail: {
                        ...ticket.detail,
                        returnDate:
                            ticket.detail.returnDate || ticket.detail.date,
                        returnTime:
                            ticket.detail.returnTime || ticket.detail.time,
                    },
                },
                isCompleted: true,
                isSubmitted: true,
                duration: '',
                unitPrice: ticket.unitPrice || null,
                defaultDates: getDatesRange(
                    parkDates.length > 0
                        ? parkDates
                        : [cartTickets[0]?.detail?.date]
                ),
                defaultQuantity: parkTickets,
            }))

            dispatch(synchronizeTicketsWithCart(routesFromCart))
        }

        // restore cabanas
        if (cabanas.length) {
            const cabanasFromCart: CabanaItemType[] = cabanas.map((cabana) => ({
                cabana: {
                    ...cabana,
                },
                isCompleted: true,
                isSubmitted: true,
            }))

            dispatch(synchronizeCabanasWithCart(cabanasFromCart))
        }

        // restore common upsells
        memoryCapture &&
            dispatch(addUpsell({ data: memoryCapture, type: 'memoryCapture' }))

        // < analytics
        pushDataLayer(
            {
                event: 'productPresented',
                ecommerce: {
                    items: [
                        {
                            item_name: 'Bus',
                            item_brand: 'PoP',
                        },
                    ],
                },
            },
            {
                event: 'productPresented',
                ecommerce: {
                    items: [
                        {
                            item_name: 'Cabana',
                            item_brand: 'PoP',
                        },
                    ],
                },
            }
        )
        // analytics >
    }, [])

    const setSpaEditing = (state: boolean) => setIsEditingSpaInProgress(state)

    return (
        <Step
            title={formatMessage({
                id: 'global.steps.caption_upsells',
                defaultMessage: 'Udogodnienia i atrakcje',
            })}
            isButtonDisabled={isCartEmpty}
            withSkipToSummaryButton={
                !isCreatorAccommodation &&
                areTicketsEmpty &&
                !isStepEmpty('upsell')
            }
        >
            <UpsellsContext.Provider
                value={{ isEditingSpaInProgress, setSpaEditing }}
            >
                {areTicketsEmpty && <AttractionsInfo />}
                <Box isFullWidth gap={[16, 24]}>
                    <AvailableOnline />
                    <Text variant="heading2" pt={16}>
                        <FormattedMessage
                            id="upsells.available_on_site_caption"
                            defaultMessage="Dostępne na miejscu"
                        />
                    </Text>
                    <AvailableOnSite />
                    <NotFinishedUpsellInfo />
                </Box>
            </UpsellsContext.Provider>
        </Step>
    )
}

export default Upsell
